import Patient from "../Crud/Patient";
import Provider from "../Crud/Provider";
import Pdf from "../Pdf";

export default class PdfDswo extends Pdf {
  constructor(data = {}, options = {}) {
    super(data, options);
  }

  /**
   * Renders both the filled and blank versions of the PDF document.
   */
  async render() {
    await this.fontLoadPromise;
    const products = this.data.orderProducts || [];
    const hasComplexMwc = products.some((p) => p.productTypeId === 10);

    this.renderDswoPage(false);
    if (!hasComplexMwc) {
      this.document.addPage();
      this.renderDswoPage(true);
    }
  }

  /**
   * Renders a DSWO page, either filled or blank.
   * @param {boolean} blank - If true, renders blank version.
   */
  renderDswoPage(blank) {
    let x = this.margin.left;
    let y = this.document.internal.pageSize.height;
    let height =
      this.document.internal.pageSize.height -
      (this.margin.top + this.margin.bottom);
    let width = this.document.internal.pageSize.width - this.margin.left * 2;
    const products = this.data.orderProducts || [];
    const hasComplexMwc = products.some((p) => p.productTypeId === 10);

    // Use different title for blank version
    this.renderHeader({
      title: blank
        ? "Standard Written Order"
        : "Detailed Standard Written Order",
    });

    let order = {};
    let patient = {};
    if (this.data.orderActivity) {
      order = this.data.orderActivity;
    } else if (this.data.originalChart) {
      order = this.data.originalChart?.orderActivity?.[this.data.orderId] || {};
      patient = this.data.originalChart?.patient?.[order.patientId] || {};
    }

    let physicianName = "";
    let physicianNpi = "";
    if (!blank) {
      if (order.orderingProviderNotFound) {
        try {
          const json = JSON.parse(
            order.jsonOrderingProviderNotFoundData || "{}",
          );
          physicianName = json.name || "";
          physicianNpi = json.npi || "";
        } catch (e) {
          console.warn("Failed to parse jsonOrderingProviderNotFoundData:", e);
        }
      } else if (this.data.originalChart.provider[order.orderingProviderId]) {
        physicianName =
          Provider.getDisplayName(
            this.data.originalChart.provider[order.orderingProviderId],
          ) || "";
        physicianNpi =
          this.data.originalChart.provider[order.orderingProviderId]?.npi || "";
      } else {
        physicianName = Provider.getDisplayName(order.orderingProvider) || "";
        physicianNpi = order.orderingProvider?.npi || "";
      }
    }

    // Build input grid data, add Length of Need and Diagnosis Codes only for blank
    const inputGridData = [
      {
        label: "Patient Name",
        data: blank ? "" : Patient.getDisplayName(patient) || "",
      },
      { label: "Physician Name", data: blank ? "" : physicianName },
    ];

    // Only include MBI and NPI if not blank
    if (!blank) {
      if (this.data.originalChart) {
        inputGridData.push({
          label: "Patient MBI",
          data: order?.mbi || patient?.mbi || "",
        });
      } else {
        inputGridData.push({
          label: "Patient MBI",
          data: order.patient?.mbi || "",
        });
      }
      inputGridData.push({ label: "Physician NPI", data: physicianNpi || "" });
    }
    // Only include Diagnosis Codes for blank version
    if (blank || hasComplexMwc) {
      inputGridData.push({ label: "Diagnosis Codes", data: "" });
      inputGridData.push({ label: "Length of Need", data: "" });
    }

    const tableRows = blank
      ? Array.from({ length: 1 }, () => [""]) // Only one column for blank
      : (this.data.orderProducts || [])
          .filter((product) => product.billable === true)
          .map((product) => [
            Pdf.context().productSubTypesIndexed?.[product.productSubTypeId]
              ?.name || "Product Description",
            product.hcpcs,
            product.qty,
          ]);

    y = this.renderInputGrid(inputGridData);

    y = this.renderTable({
      columns: blank
        ? [{ label: "Item Description", columnWidth: 600 }]
        : [
            { label: "Item Description", columnWidth: 400 },
            { label: "HCPCS", columnWidth: 100 },
            { label: "Qty", columnWidth: 100 },
          ],
      rows: tableRows,
      x: this.margin.left,
      y: y,
      blank: blank,
    });

    // Signature and date fields
    this.renderInput("Physician Signature", "", x, height, width * 0.6);
    this.renderInput("Date", "", x + width * 0.7, height, width * 0.3);
  }

  /**
   * Renders label-data pairs, two per line.
   *
   * @param {Array} labelsData - Array of objects with `label` and `data` keys.
   */
  renderInputGrid(labelsData) {
    const { document, margin } = this;
    const lineHeight = 40;
    const columnGap = 60;
    const pageWidth = document.internal.pageSize.width;
    const lineLength = (pageWidth - margin.left * 2 - columnGap) / 2;

    let x1 = margin.left;
    let x2 = margin.left + lineLength + columnGap;
    let y = 110;

    for (let i = 0; i < labelsData.length; i += 2) {
      // Draw first label-data pair
      const left = labelsData[i];
      this.renderInput(left.label, left.data, x1, y, lineLength, {
        labelFontSize: 10,
        labelFontStyle: "normal",
        valueFontSize: 10,
        valueFontStyle: "normal",
      });

      // Draw second label-data pair if it exists
      const right = labelsData[i + 1];
      if (right) {
        this.renderInput(right.label, right.data, x2, y, lineLength, {
          labelFontSize: 10,
          labelFontStyle: "normal",
          valueFontSize: 10,
          valueFontStyle: "normal",
        });
      }

      y += lineHeight;
    }

    return y;
  }
}
